import React, { useEffect, useState } from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  Progress,
  SimpleGrid,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Table,
  TableContainer,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import {
  fetchBarExams,
  fetchBarExamStatistics,
  fetchMyBarExamSubmission,
} from "../api/barExam";
import { hasAuthenticatedSession } from "../api";

import { applyMetadata, routeMetadata } from "../seo/metadata";

const SUBJECTS = [
  { code: "public_law", label: "공법", color: "blue" },
  { code: "criminal_law", label: "형사법", color: "red" },
  { code: "civil_law", label: "민사법", color: "purple" },
];

const formatScore = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) {
    return value;
  }

  return (Math.round((numericValue + Number.EPSILON) * 10) / 10).toFixed(1);
};

const getScoreDifference = (score, average) => {
  const difference = Number(score) - Number(average);

  if (Number.isNaN(difference)) {
    return null;
  }

  return Math.round((difference + Number.EPSILON) * 10) / 10;
};

const SummaryCard = ({ label, value, suffix = "", color = "blue" }) => (
  <Box
    p="5"
    rounded="lg"
    borderWidth="1px"
    borderTopWidth="4px"
    borderTopColor={`${color}.400`}
    bg="white"
    textAlign="center"
  >
    <Text color="gray.500" fontSize="sm" mb="2">
      {label}
    </Text>
    <Text color={`${color}.600`} fontSize="2xl" fontWeight="bold">
      {value ?? "-"}
      {value !== null && value !== undefined ? suffix : ""}
    </Text>
  </Box>
);

const Section = ({ title, description, children }) => (
  <Box p={{ base: 4, md: 6 }} rounded="lg" shadow="md" bg="white" mb="6">
    <Heading as="h2" size="md" mb={description ? "1" : "4"}>
      {title}
    </Heading>
    {description && (
      <Text color="gray.500" fontSize="sm" mb="4">
        {description}
      </Text>
    )}
    {children}
  </Box>
);

const MyScoreComparison = ({ submission, ranking, summary }) => {
  const scoreRows = [
    {
      label: "공법",
      score: submission.public_law_score,
      average: summary.avg_public_law_score,
    },
    {
      label: "형사법",
      score: submission.criminal_law_score,
      average: summary.avg_criminal_law_score,
    },
    {
      label: "민사법",
      score: submission.civil_law_score,
      average: summary.avg_civil_law_score,
    },
    {
      label: "총점",
      score: submission.total_score,
      average: summary.avg_total_score,
    },
  ];

  return (
    <>
      <SimpleGrid columns={{ base: 1, sm: 3 }} spacing="4" mb="5">
        <SummaryCard
          label="내 총점"
          value={formatScore(submission.total_score)}
          suffix="점"
          color="blue"
        />
        <SummaryCard
          label="예상 등수"
          value={ranking?.estimated_rank}
          suffix="위"
          color="orange"
        />
        <SummaryCard
          label="백분위"
          value={ranking?.percentile}
          suffix="%"
          color="purple"
        />
      </SimpleGrid>

      <TableContainer overflowX="auto">
        <Table variant="simple" size="sm">
          <Thead bg="gray.50">
            <Tr>
              <Th>과목</Th>
              <Th isNumeric>내 점수</Th>
              <Th isNumeric>전체 평균</Th>
              <Th isNumeric>평균 대비</Th>
            </Tr>
          </Thead>
          <Tbody>
            {scoreRows.map((row) => {
              const difference = getScoreDifference(row.score, row.average);

              return (
                <Tr key={row.label}>
                  <Td fontWeight="bold">{row.label}</Td>
                  <Td isNumeric>{formatScore(row.score)}점</Td>
                  <Td isNumeric>{formatScore(row.average)}점</Td>
                  <Td isNumeric>
                    {difference === null ? (
                      "-"
                    ) : (
                      <Badge colorScheme={difference >= 0 ? "green" : "red"}>
                        {difference > 0 ? "+" : ""}
                        {formatScore(difference)}점
                      </Badge>
                    )}
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </TableContainer>
    </>
  );
};

const RateBadge = ({ value, type = "wrong" }) => {
  const colorScheme =
    type === "correct"
      ? value >= 70
        ? "green"
        : value >= 40
          ? "yellow"
          : "red"
      : value >= 70
        ? "red"
        : value >= 40
          ? "orange"
          : "green";

  return (
    <Badge colorScheme={colorScheme} fontSize="xs" px="2" py="1">
      {value ?? 0}%
    </Badge>
  );
};

const ChoiceDistribution = ({ row, selectedChoice }) => (
  <Wrap spacing="1" minW="190px">
    {[1, 2, 3, 4, 5].map((choice) => {
      const isCorrectChoice = choice === row.correct_choice;
      const isMyWrongChoice =
        choice === selectedChoice && selectedChoice !== row.correct_choice;

      return (
        <WrapItem key={choice}>
          <Badge
            colorScheme={
              isMyWrongChoice ? "red" : isCorrectChoice ? "green" : "gray"
            }
            variant={isMyWrongChoice || isCorrectChoice ? "solid" : "subtle"}
            px="2"
            py="1"
          >
            {choice}번 {row.choices?.[choice] ?? 0}명{choice === selectedChoice}
          </Badge>
        </WrapItem>
      );
    })}
  </Wrap>
);

const DifficultQuestionTable = ({ rows = [], showSubject = true }) => {
  if (rows.length === 0) {
    return <Text color="gray.500">표시할 통계가 없습니다.</Text>;
  }

  return (
    <TableContainer overflowX="auto">
      <Table variant="simple" size="sm">
        <Thead bg="gray.50">
          <Tr>
            <Th>순위</Th>
            {showSubject && <Th>과목</Th>}
            <Th>문항</Th>
            <Th>정답</Th>
            <Th>오답률</Th>
            <Th>오답 수</Th>
            <Th>선택지 분포</Th>
          </Tr>
        </Thead>
        <Tbody>
          {rows.map((row, index) => (
            <Tr key={row.question_id || `${row.subject_code}-${row.number}`}>
              <Td fontWeight="bold">{index + 1}</Td>
              {showSubject && <Td>{row.subject}</Td>}
              <Td>{row.number}번</Td>
              <Td>
                <Badge colorScheme="green">{row.correct_choice}번</Badge>
              </Td>
              <Td>
                <RateBadge value={row.wrong_rate} />
              </Td>
              <Td>{row.wrong_count}명</Td>
              <Td>
                <ChoiceDistribution row={row} />
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

const AttractiveWrongTable = ({ rows = [] }) => {
  if (rows.length === 0) {
    return <Text color="gray.500">표시할 통계가 없습니다.</Text>;
  }

  return (
    <TableContainer overflowX="auto">
      <Table variant="simple" size="sm">
        <Thead bg="gray.50">
          <Tr>
            <Th>순위</Th>
            <Th>과목</Th>
            <Th>문항</Th>
            <Th>정답</Th>
            <Th>가장 많이 고른 오답</Th>
            <Th>선택 인원</Th>
            <Th>선택률</Th>
          </Tr>
        </Thead>
        <Tbody>
          {rows.map((row, index) => (
            <Tr key={`${row.subject}-${row.number}`}>
              <Td fontWeight="bold">{index + 1}</Td>
              <Td>{row.subject}</Td>
              <Td>{row.number}번</Td>
              <Td>
                <Badge colorScheme="green">{row.correct_choice}번</Badge>
              </Td>
              <Td>
                <Badge colorScheme="red">{row.attractive_wrong_choice}번</Badge>
              </Td>
              <Td>{row.attractive_wrong_count}명</Td>
              <Td>
                <RateBadge value={row.attractive_wrong_rate} />
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

const MainAreaTable = ({ rows = [], myRows = [] }) => (
  <TableContainer overflowX="auto">
    <Table variant="simple" size="sm">
      <Thead bg="gray.50">
        <Tr>
          <Th>과목</Th>
          <Th>주요 영역</Th>
          <Th>문항 수</Th>
          <Th minW="190px">전체 정답률</Th>
          <Th minW="190px">내 정답률</Th>
        </Tr>
      </Thead>
      <Tbody>
        {rows.map((row) => {
          const myArea = myRows.find(
            (item) =>
              item.subject_code === row.subject_code &&
              (item.main_area || item.area || item.area_name) === row.main_area,
          );
          const myCorrectRate =
            myArea?.correct_rate ??
            myArea?.my_correct_rate ??
            myArea?.rate ??
            null;

          return (
            <Tr key={`${row.subject_code}-${row.main_area}`}>
              <Td>{row.subject}</Td>
              <Td fontWeight="bold">{row.main_area}</Td>
              <Td>{row.question_count}문제</Td>
              <Td>
                <Flex align="center" gap="3">
                  <Progress
                    value={row.correct_rate}
                    colorScheme="blue"
                    rounded="full"
                    size="sm"
                    flex="1"
                  />
                  <RateBadge value={row.correct_rate} type="correct" />
                </Flex>
              </Td>
              <Td>
                {myCorrectRate === null ? (
                  "-"
                ) : (
                  <Flex align="center" gap="3">
                    <Progress
                      value={myCorrectRate}
                      colorScheme="purple"
                      rounded="full"
                      size="sm"
                      flex="1"
                    />
                    <RateBadge value={myCorrectRate} type="correct" />
                  </Flex>
                )}
              </Td>
            </Tr>
          );
        })}
      </Tbody>
    </Table>
  </TableContainer>
);

const ScoreBandTable = ({ rows = [], myScore }) => (
  <TableContainer overflowX="auto">
    <Table variant="simple" size="sm">
      <Thead bg="gray.50">
        <Tr>
          <Th>점수 구간</Th>
          <Th>인원</Th>
          <Th>평균 총점</Th>
          <Th>공법 평균</Th>
          <Th>형사법 평균</Th>
          <Th>민사법 평균</Th>
        </Tr>
      </Thead>
      <Tbody>
        {rows.map((row) => {
          const numericScore = Number(myScore);
          const bandStart = Number(row.band_start);
          const bandEnd = Number(row.band_end);
          const isMyBand =
            !Number.isNaN(numericScore) &&
            !Number.isNaN(bandStart) &&
            !Number.isNaN(bandEnd) &&
            numericScore >= bandStart &&
            numericScore <= bandEnd;

          return (
            <Tr
              key={row.band}
              bg={isMyBand ? "yellow.50" : undefined}
              borderLeftWidth={isMyBand ? "4px" : undefined}
              borderLeftColor={isMyBand ? "yellow.400" : undefined}
            >
              <Td>
                <Wrap align="center">
                  <WrapItem>
                    <Badge colorScheme="blue" px="2" py="1">
                      {row.band}점
                    </Badge>
                  </WrapItem>
                  {isMyBand && (
                    <WrapItem>
                      <Badge colorScheme="yellow">내 구간</Badge>
                    </WrapItem>
                  )}
                </Wrap>
              </Td>
              <Td fontWeight="bold">{row.count}명</Td>
              <Td>{formatScore(row.avg_total_score)}점</Td>
              <Td>{formatScore(row.avg_public_law_score)}점</Td>
              <Td>{formatScore(row.avg_criminal_law_score)}점</Td>
              <Td>{formatScore(row.avg_civil_law_score)}점</Td>
            </Tr>
          );
        })}
      </Tbody>
    </Table>
  </TableContainer>
);

const QuestionStatisticsTable = ({ rows = [], answers = [] }) => {
  const answerMap = Object.fromEntries(
    answers.map((answer) => [
      `${answer.subject_code}-${answer.number}`,
      answer,
    ]),
  );

  return (
    <TableContainer maxH="620px" overflowY="auto" overflowX="auto">
      <Table variant="simple" size="sm">
        <Thead position="sticky" top="0" bg="gray.50" zIndex="1">
          <Tr>
            <Th>문항</Th>
            <Th>정답</Th>
            <Th>내 답</Th>
            <Th>정답률</Th>
            <Th>선택지별 응답 인원</Th>
          </Tr>
        </Thead>
        <Tbody>
          {rows.map((row) => {
            const myAnswer =
              answerMap[`${row.subject_code}-${row.number}`] || null;
            const hasMyAnswer = Boolean(myAnswer);
            const selectedChoice = myAnswer?.selected_choice ?? null;
            const isCorrect =
              myAnswer?.is_correct ??
              (selectedChoice !== null &&
                Number(selectedChoice) === Number(row.correct_choice));

            return (
              <Tr
                key={row.question_id}
                bg={hasMyAnswer && !isCorrect ? "red.50" : undefined}
              >
                <Td fontWeight="bold">{row.number}번</Td>
                <Td>
                  <Badge colorScheme="green">{row.correct_choice}번</Badge>
                </Td>
                <Td>
                  {selectedChoice === null ? (
                    "-"
                  ) : (
                    <Badge colorScheme={isCorrect ? "green" : "red"}>
                      {selectedChoice}번
                    </Badge>
                  )}
                </Td>

                <Td>
                  <RateBadge value={row.correct_rate} type="correct" />
                </Td>

                <Td>
                  <ChoiceDistribution
                    row={row}
                    selectedChoice={selectedChoice}
                  />
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

const BarExamStatistics = () => {
  const { examId } = useParams();
  const navigate = useNavigate();
  const [statistics, setStatistics] = useState(null);
  const [fallbackExamTitle, setFallbackExamTitle] = useState("");
  const [mySubmission, setMySubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    setStatistics(null);
    setFallbackExamTitle("");
    setMySubmission(null);
    const loadStatistics = async () => {
      try {
        const [statisticsData, authenticated] = await Promise.all([
          fetchBarExamStatistics(examId),
          hasAuthenticatedSession(),
        ]);
        if (!active) return;
        setStatistics(statisticsData);
        setIsAuthenticated(authenticated);

        // Older deployed statistics APIs omit exam metadata. Reuse the public
        // exam list without making its availability a prerequisite for statistics.
        if (!statisticsData?.exam?.title?.trim()) {
          fetchBarExams()
            .then((data) => {
              if (!active) return;
              const exams = Array.isArray(data) ? data : data?.results || [];
              const exam = exams.find((item) => String(item.id) === String(examId));
              setFallbackExamTitle(exam?.title?.trim() || "");
            })
            .catch(() => {
              // The existing statistics and personal comparison remain usable.
            });
        }

        if (authenticated) {
          try {
            const submission = await fetchMyBarExamSubmission(examId);
            if (active) setMySubmission(submission);
          } catch (submissionError) {
            console.error("Error fetching my submission:", submissionError);
            if (active) setMySubmission({ exists: false, submission: null });
          }
        } else {
          setMySubmission({ exists: false, submission: null });
        }
      } catch (error) {
        console.error("Error fetching bar exam statistics:", error);
        if (active) setError(error.data?.detail || "시험 통계를 불러오지 못했습니다.");
      } finally {
        if (active) setLoading(false);
      }
    };

    loadStatistics();
    return () => { active = false; };
  }, [examId]);

  const examTitle = statistics?.exam?.title?.trim() || fallbackExamTitle || "변호사시험 모의고사";
  const hasData = Number(statistics?.summary?.submitter_count) > 0;
  useEffect(() => {
    const metadata = routeMetadata(`/bar-exam/${examId}/statistics`);
    if (loading) {
      applyMetadata(metadata);
      return;
    }
    applyMetadata({
      ...metadata,
      title: `${examTitle} 통계·문항 분석 | IBT 연습장`,
      description: `${examTitle}의 제출 답안을 집계한 응시자 수, 평균 점수, 점수 분포와 문항별 정답률입니다. 내 성적과 비교해 복습할 문항을 확인하세요.`,
      index: !error && hasData,
    });
  }, [examId, examTitle, loading, error, hasData]);

  if (loading) {
    return (
      <Flex
        align="center"
        justify="center"
        minH="100vh"
        bgGradient="linear(to-r, blue.50, blue.100)"
      >
        <Spinner size="xl" color="blue.500" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex
        direction="column"
        align="center"
        justify="center"
        minH="100vh"
        bgGradient="linear(to-r, blue.50, blue.100)"
        p={4}
      >
        <Alert status="error" rounded="md" maxW="600px" mb="4">
          <AlertIcon />
          {error}
        </Alert>
        <Button colorScheme="blue" onClick={() => navigate("/bar-exam")}>
          시험 목록으로
        </Button>
      </Flex>
    );
  }

  if (!hasData) {
    return <Flex as="main" direction="column" align="center" p="8" gap="4">
      <Heading as="h1" size="lg">{examTitle} 통계</Heading>
      <Text>아직 제출 데이터가 없어 통계를 표시할 수 없습니다.</Text>
      <Text>답안이 제출되면 전체 점수와 문항별 통계를 확인할 수 있습니다.</Text>
      <Button as={RouterLink} to="/bar-exam">시험 목록으로</Button>
      <Button as={RouterLink} to="/guide" variant="link">통계 이용 가이드</Button>
    </Flex>;
  }

  const summary = statistics?.summary || {};
  const questionStatistics = statistics?.question_statistics || [];
  const wrongRateBySubject = statistics?.wrong_rate_top5_by_subject || {};

  return (
    <Flex
      as="main"
      direction="column"
      align="center"
      minH="100vh"
      bgGradient="linear(to-r, blue.50, blue.100)"
      p={{ base: 4, md: 8 }}
    >
      <Box width="full" maxW="1200px">
        <Heading as="h1" mb="2" textAlign="center">
          {examTitle} 통계
        </Heading>
        <Text color="gray.600" textAlign="center" mb="8">
          전체 응시 결과와 과목·영역·문항별 분석을 확인하세요.
        </Text>

        <Section title="이 통계는 어떤 자료인가요?">
          <Text mb="3">{examTitle}에 제출된 답안을 IBT 연습장에서 집계한 결과입니다. 현재 점수 통계에는 {summary.submitter_count}명의 제출 내역이 포함되어 있습니다. 실제 시험 전체 응시자의 공식 성적이나 합격 예측을 뜻하지 않습니다.</Text>
          <Text mb="3">점수 요약과 등수는 삭제 처리되지 않은 제출 내역을 기준으로 합니다. 문항 통계는 활성 문항에 저장된 응답을 집계하므로 문항별 응답 수와 점수 통계의 응시자 수가 다를 수 있습니다. 참여와 답안 수정에 따라 수치는 달라집니다.</Text>
          <Button as={RouterLink} to="/guide#compare" variant="link" colorScheme="blue">점수 비교와 통계 활용 가이드</Button>
        </Section>

        <Section
          title="내 성적과 전체 평균 비교"
          description="내 점수와 제출자 평균의 차이를 비교합니다. 예상 등수는 나보다 높은 점수의 제출자 수에 1을 더한 값이며, 백분위는 내 점수 이하인 제출자의 비율입니다."
        >
          {mySubmission?.exists ? (
            <MyScoreComparison
              submission={mySubmission.submission}
              ranking={mySubmission.ranking}
              summary={summary}
            />
          ) : (
            <Alert status="info" rounded="md">
              <AlertIcon />
              <Box flex="1">
                <Text>
                  {isAuthenticated
                    ? "제출한 답안이 없어 내 성적을 표시할 수 없습니다."
                    : "전체 통계는 로그인 없이 볼 수 있습니다. 내 성적 비교는 로그인 후 답안을 제출하면 확인할 수 있습니다."}
                </Text>
              </Box>
              {!isAuthenticated && (
                <Button
                  size="sm"
                  colorScheme="blue"
                  ml="3"
                  onClick={() =>
                    navigate("/login", {
                      state: {
                        returnTo: `/bar-exam/${examId}/statistics`,
                      },
                    })
                  }
                >
                  로그인
                </Button>
              )}
            </Alert>
          )}
        </Section>

        <Section title="전체 성적 요약" description="같은 시험에 답안을 제출한 이용자의 총점과 과목별 점수입니다. 평균과 함께 참여 인원도 확인하세요.">
          <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing="4" mb="4">
            <SummaryCard
              label="응시자 수"
              value={summary.submitter_count}
              suffix="명"
              color="teal"
            />
            <SummaryCard
              label="평균 총점"
              value={formatScore(summary.avg_total_score)}
              suffix="점"
            />
            <SummaryCard
              label="최고점"
              value={formatScore(summary.max_total_score)}
              suffix="점"
              color="green"
            />
            <SummaryCard
              label="최저점"
              value={formatScore(summary.min_total_score)}
              suffix="점"
              color="orange"
            />
          </SimpleGrid>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing="4">
            <SummaryCard
              label="공법 평균"
              value={formatScore(summary.avg_public_law_score)}
              suffix="점"
              color="blue"
            />
            <SummaryCard
              label="형사법 평균"
              value={formatScore(summary.avg_criminal_law_score)}
              suffix="점"
              color="red"
            />
            <SummaryCard
              label="민사법 평균"
              value={formatScore(summary.avg_civil_law_score)}
              suffix="점"
              color="purple"
            />
          </SimpleGrid>
        </Section>

        <Section
          title="주요 영역별 성취도"
          description="각 법 과목의 세부 영역별 정답률입니다. 내 정답률이 전체보다 낮은 영역에서 틀린 문항을 다시 확인해 복습 범위를 정해보세요."
        >
          <MainAreaTable
            rows={statistics?.main_area_statistics || []}
            myRows={mySubmission?.analysis?.main_area_analysis || []}
          />
        </Section>

        <Section
          title="점수 구간별 분포"
          description="총점 구간별 제출 인원과 과목 평균입니다. 내 점수 구간과 인접 구간의 과목 평균을 비교해 보완할 과목을 살펴보세요. 표본이 적은 구간은 일부 응시자의 영향이 클 수 있습니다."
        >
          <ScoreBandTable
            rows={statistics?.score_band_statistics || []}
            myScore={mySubmission?.submission?.total_score}
          />
        </Section>

        <Section
          title="전체 오답률 TOP 5"
          description="전체 과목에서 오답률이 높은 문항입니다. 나도 틀렸다면 원래 문제와 해설에서 선지 판단 근거를 다시 확인해보세요."
        >
          <DifficultQuestionTable rows={statistics?.wrong_rate_top5 || []} />
        </Section>

        <Section
          title="과목별 오답률 TOP 5"
          description="과목별로 가장 어려웠던 문항을 따로 확인할 수 있습니다."
        >
          <Tabs colorScheme="blue" variant="soft-rounded" isFitted>
            <TabList mb="4">
              {SUBJECTS.map((subject) => (
                <Tab key={subject.code}>{subject.label}</Tab>
              ))}
            </TabList>
            <TabPanels>
              {SUBJECTS.map((subject) => (
                <TabPanel key={subject.code} px="0">
                  <DifficultQuestionTable
                    rows={wrongRateBySubject[subject.code] || []}
                    showSubject={false}
                  />
                </TabPanel>
              ))}
            </TabPanels>
          </Tabs>
        </Section>

        <Section
          title="매력적 오답 TOP 5"
          description="정답이 아닌 선지 중 많이 선택된 오답입니다. 선택률은 해당 문항의 전체 응답 중 그 선지를 고른 비율입니다. 내 선택과 비교해 혼동한 개념을 점검하세요."
        >
          <AttractiveWrongTable
            rows={statistics?.attractive_wrong_top5 || []}
          />
        </Section>

        <Section
          title="문항별 상세 통계"
          description="정답률은 해당 문항의 저장된 응답 중 정답으로 채점된 비율입니다. 과목 탭에서 모든 문항을 확인하고, 정답률이 높은데 내가 틀린 문항부터 입력 실수나 개념을 점검해보세요."
        >
          <Tabs colorScheme="blue" variant="enclosed" isFitted>
            <TabList>
              {SUBJECTS.map((subject) => (
                <Tab key={subject.code}>{subject.label}</Tab>
              ))}
            </TabList>
            <TabPanels>
              {SUBJECTS.map((subject) => (
                <TabPanel key={subject.code} px="0">
                  <QuestionStatisticsTable
                    rows={questionStatistics.filter(
                      (row) => row.subject_code === subject.code,
                    )}
                    answers={mySubmission?.submission?.answers || []}
                  />
                </TabPanel>
              ))}
            </TabPanels>
          </Tabs>
        </Section>

        <Button
          colorScheme="blue"
          size="lg"
          width="full"
          onClick={() => navigate("/bar-exam")}
        >
          시험 목록으로
        </Button>
      </Box>
    </Flex>
  );
};

export default BarExamStatistics;
